import { SheetModeControls } from "@/components/r3f/controls/sheet-mode-controls";
import { DesaturationSheetsPipeline } from "@/components/r3f/renderers/desaturation-pipeline";
import { SheetRenderer } from "@/components/r3f/renderers/sheet-renderer";
import {
  centerOrthoCamera,
  useCenterCameraOnPlaceholders,
  useOrthoCameraRotationFromSheet,
} from "@/hooks/use-center-camera-on-placeholders";
import { useIElementClippingBoxObb } from "@/hooks/use-object-bounding-box";
import { IElementWaypoint } from "@/hooks/use-placeholders";
import { useCached3DObjectsIfExists } from "@/object-cache";
import { RootState } from "@/store/store";
import { useAppSelector } from "@/store/store-hooks";
import {
  MapWaypointsRendererRef,
  useNonExhaustiveEffect,
  useReproportionCamera,
} from "@faro-lotv/app-component-toolbox";
import { assert, GUID } from "@faro-lotv/foundation";
import {
  IElementSection,
  isIElementAreaSection,
  isIElementImg360,
  isValid,
} from "@faro-lotv/ielement-types";
import { FloorPlanBackgroundTransparency } from "@faro-lotv/lotv";
import {
  selectChildDepthFirst,
  selectIElement,
  selectMainAreaVolume,
} from "@faro-lotv/project-source";
import { useThree } from "@react-three/fiber";
import { createSelector } from "@reduxjs/toolkit";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";
import { OrthographicCamera } from "three";
import { selectCurrentAreaData } from "../select-current-area-data";
import { SheetWaypoints } from "../sheet-mode/sheet-waypoints";
import { useWaypointsSelectedTextures } from "../sheet-mode/use-waypoints-selected-textures";
import { WaypointPreview } from "../sheet-mode/waypoint-preview";
import { useTagsManagementContext } from "./tags-management-context";
import {
  selectTagsManagementScene,
  TagsManagementScene as TagsManagementCurrentScene,
} from "./tags-management-selector";

type IndexMap = Record<GUID, number>;

/** @returns The 3D scene of the tags management mode */
export function TagsManagementScene(): JSX.Element | null {
  const {
    activeAreaId,
    hoveredIds,
    hoverId,
    clearHovering,
    selectedIds,
    toggleSelectedId,
    selectId,
    visibleSheets,
    setSheetVisibility,
  } = useTagsManagementContext();
  const activeArea = useAppSelector(selectIElement(activeAreaId));
  assert(
    activeArea && isIElementAreaSection(activeArea),
    "There should always be an area active",
  );

  const areaData = useAppSelector(selectCurrentAreaData(activeArea), isEqual);
  const currentScene = useAppSelector(
    selectTagsManagementScene(areaData),
    isEqual,
  );

  const areaVolume = useAppSelector(selectMainAreaVolume(activeArea));
  const areaBox = useIElementClippingBoxObb(areaVolume);

  const panos = useAppSelector(
    (state) => selectScenePanos(state, currentScene),
    isEqual,
  );
  const { locationMarkers, photos } = currentScene;

  const placeholders = useMemo(
    () => [...panos, ...locationMarkers, ...photos],
    [locationMarkers, panos, photos],
  );

  const activeSheets = useMemo(
    () =>
      visibleSheets.filter(
        (visibleSheet) =>
          currentScene.sheets.findIndex(
            (sheet) => sheet.id === visibleSheet.id,
          ) >= 0,
      ),
    [currentScene.sheets, visibleSheets],
  );

  // If no sheet is visible, show the first of the available ones
  useEffect(() => {
    if (activeSheets.length === 0 && currentScene.sheets.length > 0) {
      setSheetVisibility(currentScene.sheets[0], true);
    }
  }, [activeSheets.length, currentScene.sheets, setSheetVisibility]);

  const sections = useMemo(
    () => [
      ...currentScene.rooms,
      ...Object.values(currentScene.scans).flat(),
      ...currentScene.locationMarkers,
      ...currentScene.photoSections,
    ],
    [currentScene],
  );

  const { placeholderToIndex, sectionToIndex } = useMemo(() => {
    const placeholderToIndex: IndexMap = {};
    const sectionToIndex: IndexMap = {};
    for (let index = 0; index < placeholders.length; ++index) {
      placeholderToIndex[placeholders[index].id] = index;
      sectionToIndex[sections[index].id] = index;
    }
    return { placeholderToIndex, sectionToIndex };
  }, [placeholders, sections]);

  const cameraRotation = useOrthoCameraRotationFromSheet(
    activeSheets.at(0)?.id,
  );
  const cameraData = useCenterCameraOnPlaceholders({
    areaVolume: areaBox,
    sheetElements: activeSheets,
    cameraRotation,
    placeholders: panos,
  });

  const camera = useThree((s) => s.camera);
  useReproportionCamera(camera);
  useNonExhaustiveEffect(() => {
    if (camera instanceof OrthographicCamera) {
      centerOrthoCamera(camera, cameraData);
    }
  }, [activeAreaId, camera, currentScene.sheets]);

  const [mapPlaceholders, setMapPlaceholders] =
    useState<MapWaypointsRendererRef | null>(null);
  useTagsManagementWaypointsSelected(
    mapPlaceholders,
    selectedIds,
    sectionToIndex,
  );
  useTagsManagementWaypointsHovered(
    mapPlaceholders,
    hoveredIds,
    sectionToIndex,
  );

  const onPlaceholderClick = useCallback(
    (waypoint: IElementWaypoint, event: MouseEvent) => {
      const scanIndex = placeholderToIndex[waypoint.id];
      if (scanIndex < 0) return;

      const scan = sections[scanIndex];
      if (event.ctrlKey || event.shiftKey || event.metaKey) {
        toggleSelectedId(scan.id);
      } else {
        selectId(scan.id);
      }
    },
    [placeholderToIndex, sections, toggleSelectedId, selectId],
  );

  const [previewElement, setPreviewElement] = useState<IElementWaypoint>();
  const onPlaceholderHovered = useCallback(
    (waypoint?: IElementWaypoint) => {
      if (!waypoint) {
        clearHovering();
        setPreviewElement(undefined);
        return;
      }
      const scanIndex = placeholderToIndex[waypoint.id];
      if (scanIndex < 0) return;

      const scan = sections[scanIndex];
      hoverId(scan.id);
      setPreviewElement(waypoint);
    },
    [placeholderToIndex, sections, hoverId, clearHovering],
  );

  const { selectedTexture, selectedFlashScanTexture } =
    useWaypointsSelectedTextures();

  const sheetObjects = useCached3DObjectsIfExists(
    activeSheets,
    "TagsManagementScene",
  );
  return (
    <>
      {sheetObjects.map((sheet, index) => (
        <SheetRenderer
          sheet={sheet}
          backgroundTransparent={FloorPlanBackgroundTransparency.Alpha}
          key={sheet.iElement.id}
          index={index}
        />
      ))}
      <SheetModeControls camera={camera} referencePlaneHeight={0} />
      <SheetWaypoints
        paths={[]}
        placeholders={placeholders}
        ref={setMapPlaceholders}
        onPlaceholderClicked={onPlaceholderClick}
        onPlaceholderHovered={onPlaceholderHovered}
        textures={{ selectedTexture }}
        flashTextures={{ selectedTexture: selectedFlashScanTexture }}
      />
      <DesaturationSheetsPipeline enabled={false} transparentBackground />
      {previewElement && <WaypointPreview placeholder={previewElement} />}
    </>
  );
}

/* eslint-disable jsdoc/require-param */
/**
 * @returns The collection of iElementImg360 children of the current scene
 * @param currentScene The current scene of the tags management mode
 */
const selectScenePanos = createSelector(
  [
    (state: RootState) => state,
    (state: RootState, currentScene: TagsManagementCurrentScene) =>
      currentScene.rooms,
    (state: RootState, currentScene: TagsManagementCurrentScene) =>
      currentScene.scans,
  ],
  (
    state: RootState,
    rooms: IElementSection[],
    scans: Record<string, IElementSection[]>,
  ) =>
    [...rooms, ...Object.values(scans).flat()]
      .map((e) => selectChildDepthFirst(e, isIElementImg360)(state))
      .filter(isValid),
);

/**
 * Manually update the state of the placeholders to support multi-selection.
 *
 * @param mapPlaceholders The renderer of the waypoints
 * @param selectedIds The list of ids selected by the user
 * @param sections The pano sections in the current scene
 */
function useTagsManagementWaypointsSelected(
  mapPlaceholders: MapWaypointsRendererRef | null | undefined,
  selectedIds: GUID[],
  panoToSectionIndex: Record<GUID, number>,
): void {
  useEffect(() => {
    if (!mapPlaceholders) return;
    const selectedScansIndices: number[] = [];
    for (const id of selectedIds) {
      const panoIndex = panoToSectionIndex[id];
      if (panoIndex >= 0) {
        selectedScansIndices.push(panoIndex);
      }
    }

    mapPlaceholders.selectedIds = selectedScansIndices;
  }, [mapPlaceholders, panoToSectionIndex, selectedIds]);
}

/**
 * Manually update the state of the placeholders to support multi-hovering.
 *
 * @param mapPlaceholders The renderer of the waypoints
 * @param hoveredIds The list of ids selected by the user
 * @param sections The pano sections in the current scene
 */
function useTagsManagementWaypointsHovered(
  mapPlaceholders: MapWaypointsRendererRef | null | undefined,
  hoveredIds: GUID[],
  panoToSectionIndex: Record<GUID, number>,
): void {
  useEffect(() => {
    if (!mapPlaceholders) return;
    const hoveredScansIndices: number[] = [];
    for (const id of hoveredIds) {
      const panoIndex = panoToSectionIndex[id];
      if (panoIndex >= 0) {
        hoveredScansIndices.push(panoIndex);
      }
    }
    mapPlaceholders.hoveredIds = hoveredScansIndices;
  }, [mapPlaceholders, panoToSectionIndex, hoveredIds]);
}
